import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';
import { AsyncPipe } from '@angular/common';
import { Subject, take } from 'rxjs';
import { SidebarHeaderComponent } from '../../sidebar-header/sidebar-header.component';
import { PrimeContractGeneralPageComponent } from './prime-contract-general-page/prime-contract-general-page.component';
import { PrimeContractSOVsComponent } from './prime-contract-values/prime-contract-sovs.component';
import { PrimeChangeOrderMarkupsComponent } from './prime-change-order-markups/prime-change-order-markups.component';
import { PrimeInvoiceRetainageComponent } from './prime-invoice-retainage/prime-invoice-retainage.component';
import { PrimeContractUploadTemplateComponent } from './prime-contract-upload-template/prime-contract-upload-template.component';
import { primeContractActions } from '@app/store/prime-commitments/prime-commitments.actions';
import {
  PrimeContractModel,
  IPrimeLineItem,
} from '@app/store/prime-commitments/prime-commitments.types';
import { DeepCopyService } from '@app/services/deep-copy.service';
import { primeSelectors } from '@app/store/prime-commitments/prime-commitments.selectors';
import { NotificationsService } from '@app/services/notifications.service';
import {
  getEmptySpendEntrySOV,
  primeContractStatuses,
} from '@app/store/prime-commitments/prime-commitments.constants';

@Component({
  selector: 'app-prime-contract-sidebar',
  imports: [
    SidebarHeaderComponent,
    FormsModule,
    PrimeContractGeneralPageComponent,
    PrimeContractSOVsComponent,
    PrimeChangeOrderMarkupsComponent,
    PrimeInvoiceRetainageComponent,
    PrimeContractUploadTemplateComponent,
    AsyncPipe,
  ],
  templateUrl: './prime-contract-sidebar.component.html',
})
export class PrimeContractSidebarComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly notif = inject(NotificationsService);
  private readonly isDestroyed$ = new Subject<void>();

  @Input() projectId: number;
  @Input() isEdit = false;

  statuses = primeContractStatuses;
  model: PrimeContractModel = {
    files: [],
    title: '',
    owner_name: '',
    date: '',
    status: '',
    description: '',
    purchase_order_number: '',
    defaultInvoiceRetainageRate: '0',
    sovs: [getEmptySpendEntrySOV()],
    markupGroups: [],
    exceptions: [],
  };

  primeContractModel$ = this.store.select(primeSelectors.selectPrimeContractModel);
  currentPage$ = this.store.select(primeSelectors.selectPrimeContractSidebarPage);

  showPrimeTemplateUploadPage = false;
  private saveClicked$ = new Subject();

  ngOnInit() {
    this.store.dispatch(primeContractActions.onPrimeContractSidebarOpen({ isEdit: this.isEdit }));
    this.primeContractModel$
      .pipe(
        filter((data) => !!data),
        takeUntil(this.isDestroyed$),
        take(1),
      )
      .subscribe({
        next: (data) => {
          this.model = DeepCopyService.deepCopy(data);
        },
      });

    this.saveClicked$.pipe(takeUntil(this.isDestroyed$), debounceTime(300)).subscribe(() => {
      this.saveContract();
    });
  }

  ngOnDestroy() {
    this.isDestroyed$.next();
    this.isDestroyed$.complete();
  }

  onSaveClicked() {
    this.saveClicked$.next(true);
  }

  saveContract() {
    if (this.model.id) {
      this.store.dispatch(
        primeContractActions.onPrimeContractUpdate({
          primeContract: DeepCopyService.deepCopy(this.model),
        }),
      );
      return;
    }

    this.store.dispatch(
      primeContractActions.onPrimeContractSubmit({
        primeContract: DeepCopyService.deepCopy(this.model),
      }),
    );
  }

  incrementPage() {
    this.store.dispatch(primeContractActions.onPrimeContractClickNext());
  }

  goToPreviousPage() {
    if (this.showPrimeTemplateUploadPage) {
      this.showPrimeTemplateUploadPage = false;
      return;
    }

    this.store.dispatch(primeContractActions.onPrimeContactPreviousPage());
  }

  /**
   * When Nic will want the upload page uncomment this and adapt to new requirements in replaceDataWithTemplate
   */
  changeViewToUploadPage() {
    this.showPrimeTemplateUploadPage = true;
  }

  // this was removed in the latest design update along with the template upload page
  replaceDataWithTemplate(templateValues: IPrimeLineItem[]) {
    if (!templateValues.length) {
      this.notif.showError('Could not identify any template.');
      return;
    }
    // this.model.lineItems = templateValues; // todo, see how this is supposed to work
    this.showPrimeTemplateUploadPage = false;
  }
}
